import { useStore } from "@/core/services/store";
import { setXentralConfig } from "@/composables/useSalesChannelManagementApp";
import { useRoute } from "vue-router/composables";
import { isEmpty } from "lodash";
import { Toast } from "@/core/plugins/swal";
import i18n from "@/core/plugins/vue-i18n";
import { computed } from "vue";
import { useMigrationIntegration } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useMigrationIntegration";
import { useApp } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useApp";
import useOauth from "@/components/ExternalApps/SalesChannelManagementApp/composables/useOauth";

export const useEbayOauth = () => {
  const store = useStore();
  const route = useRoute();
  const { base64ToUtf8, utf8ToBase64 } = useOauth();

  const xentralConfigData = computed(
    () => store.getters.xentralConfig?.data ?? []
  );
  const queryState = computed(() => {
    return base64ToUtf8(xentralConfigData.value?.["state"]);
  });

  const { selectIntegration } = useApp();
  const { show, startMigration, selectedChannel, selectedChannelId } =
    useMigrationIntegration();

  async function getAccessToken() {
    const state = store.getters.migrationOAuthState;
    const xentralConfig = store.getters.xentralConfig?.data;
    const fromUrl = xentralConfig?.baseUrl?.replace(/\/$/, "") || "";
    const endpoint = xentralConfig?.xentralAppUri || "";

    Object.assign(state, {
      migration: true,
      isMigration: true,
      softMigrated: true,
      from_url: `${fromUrl.replace(/\/+$/, "")}/${endpoint.replace(
        /^\/+/,
        ""
      )}`,
      redirect_uri: store.getters.oauthVariable("migration_redirect_uri"),
      licenseId: store.getters.licenseId,
      origin: route.params?.area,
      uuid: selectedChannel.value?.uuid,
      project_id: store.getters?.selectedIntegration?.id
    });

    const params = new URLSearchParams({
      client_id: store.getters.oAuthClientId,
      redirect_uri: store.getters.oauthVariable("migration_redirect_uri"),
      response_type: store.getters.oauthVariable("response_type"),
      scope: store.getters.oauthVariable("scope"),
      // Forces the eBay login screen, see ebayApp config
      prompt: store.getters.oauthVariable("prompt"),
      state: utf8ToBase64(state)
    });

    resetXentralConfig();

    window.open(`https://auth.ebay.com/oauth2/authorize?${params}`, "_parent");
  }

  async function startEbayOAuthMigration() {
    if (isEmpty(queryState.value)) return;

    Toast.fire({
      icon: "success",
      title: i18n.t("salesChannelManagementApp.ebay.migrationSuccess")
    }).then(() => {
      // ignore promise
    });

    selectedChannelId.value.channelId = queryState.value.uuid;
    await startMigration(queryState.value);
    resetXentralConfig();
    await store.dispatch("setMigrationOAuthState", undefined);
  }

  async function applyEbayOAuthValues() {
    if (isEmpty(queryState.value)) return;

    if (queryState.value?.migration) {
      show.value = true;
      return;
    }

    const selectedIntegration = store.getters.integrationProject(
      queryState.value.project_id
    );
    await selectIntegration(
      selectedIntegration,
      "settings",
      "settings",
      "wizard"
    );

    await Toast.fire({
      icon: "success",
      title: i18n.t("salesChannelManagementApp.newTokenSuccess")
    });

    resetXentralConfig();
  }

  function resetXentralConfig() {
    let xentralData = store.getters.xentralConfig;
    let newData = { ...xentralData.data };
    delete newData.state;
    let updatedXentralConfig = { ...xentralData, data: newData };
    setXentralConfig(updatedXentralConfig);
  }

  return {
    startEbayOAuthMigration,
    applyEbayOAuthValues,
    getAccessToken
  };
};
